import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import mqtt from 'mqtt';

const MQTTContext = createContext();

export const MQTTProvider = ({ children }) => {
  const [client, setClient] = useState(null);
  const [connected, setConnected] = useState(false);
  const [sensorData, setSensorData] = useState({
    temperature: 0,
    humidity: 0,
    vibration: 0,
    // Synthetic until hardware is added — replace by subscribing to extra topics
    battery: 92,        // percent
    motion: 0,          // 0|1 — motion detected
    gps: {
      latitude: 0,
      longitude: 0,
      accuracy: null,
      speed: null,
      heading: null,
      source: 'none',
      updatedAt: null
    },
    rssi: 0,
    timestamp: new Date()
  });
  const [geoStatus, setGeoStatus] = useState('idle'); // idle|locating|live|denied|unavailable|error
  const [history, setHistory] = useState([]);
  const [status, setStatus] = useState('Disconnected');

  // HiveMQ public broker. On HTTPS pages we MUST use wss:// (mixed-content rule),
  // on plain HTTP localhost dev we can use ws://.
  const isSecure = typeof window !== 'undefined' && window.location.protocol === 'https:';
  const MQTT_BROKER =
    process.env.REACT_APP_MQTT_URL ||
    (isSecure
      ? 'wss://broker.hivemq.com:8884/mqtt'
      : 'ws://broker.hivemq.com:8000/mqtt');

  // Matches the ESP8266 sketch (esp8266_adxl_dht11.ino) which publishes a
  // single combined JSON to "container/sensors".
  const TOPICS = {
    sensors: process.env.REACT_APP_MQTT_TOPIC || 'container/sensors',
    gps:     'container/sensor/gps',
    status:  'container/status'
  };

  useEffect(() => {
    let mqttClient;
    try {
      mqttClient = mqtt.connect(MQTT_BROKER, {
        reconnectPeriod: 2000,
        connectTimeout: 8000,
        clean: true,
        clientId: `ContainerMonitor_${Math.random().toString(16).slice(2)}`
      });
    } catch (err) {
      console.error('Failed to construct MQTT client:', err);
      setStatus('Connection failed: ' + err.message);
      return;
    }

    mqttClient.on('connect', () => {
      console.log('Connected to MQTT broker');
      setConnected(true);
      setStatus('Connected');

      Object.values(TOPICS).forEach(topic => {
        mqttClient.subscribe(topic, (err) => {
          if (err) console.error(`Failed to subscribe to ${topic}:`, err);
        });
      });
    });

    mqttClient.on('message', (topic, message) => {
      let data;
      try {
        data = JSON.parse(message.toString());
      } catch (err) {
        console.error('Failed to parse message:', err, message.toString());
        return;
      }

      setSensorData(prev => {
        const updated = { ...prev, timestamp: new Date() };

        if (topic === TOPICS.sensors) {
          // Combined payload from ESP8266
          if (data.temp     !== undefined) updated.temperature = parseFloat(data.temp);
          if (data.humidity !== undefined) updated.humidity    = parseFloat(data.humidity);
          // Use accel magnitude as vibration value
          if (data.accel_mag !== undefined) updated.vibration  = parseFloat(data.accel_mag);
          else if (data.vibration !== undefined) updated.vibration = parseFloat(data.vibration);
        } else if (topic === TOPICS.gps) {
          updated.gps = {
            latitude:  parseFloat(data.lat) || 0,
            longitude: parseFloat(data.lon) || 0
          };
        }

        setHistory(h => [...h.slice(-59), { ...updated, timestamp: new Date() }]);
        return updated;
      });
    });

    mqttClient.on('error', (err) => {
      console.error('MQTT error:', err);
      setStatus('Error: ' + (err && err.message ? err.message : 'unknown'));
    });

    mqttClient.on('reconnect', () => setStatus('Reconnecting...'));
    mqttClient.on('close',     () => { setConnected(false); setStatus('Disconnected'); });
    mqttClient.on('offline',   () => { setConnected(false); setStatus('Offline'); });

    setClient(mqttClient);

    return () => {
      try { mqttClient.end(true); } catch (_) {}
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ===== Browser geolocation: live tracking =====
  useEffect(() => {
    if (!('geolocation' in navigator)) {
      setGeoStatus('unavailable');
      return;
    }
    setGeoStatus('locating');

    const onPosition = (pos) => {
      const { latitude, longitude, accuracy, speed, heading } = pos.coords;
      setGeoStatus('live');
      setSensorData(prev => ({
        ...prev,
        gps: {
          latitude,
          longitude,
          accuracy: accuracy ?? null,
          speed:    speed ?? null,
          heading:  heading ?? null,
          source:   'browser',
          updatedAt: new Date()
        }
      }));
    };

    const onError = (err) => {
      console.warn('Geolocation error:', err);
      if (err.code === err.PERMISSION_DENIED)      setGeoStatus('denied');
      else if (err.code === err.POSITION_UNAVAILABLE) setGeoStatus('unavailable');
      else                                          setGeoStatus('error');
    };

    const watchId = navigator.geolocation.watchPosition(onPosition, onError, {
      enableHighAccuracy: true,
      maximumAge: 2000,
      timeout: 15000
    });

    return () => navigator.geolocation.clearWatch(watchId);
  }, []);

  // ===== Synthetic battery / gas / motion stream =====
  // Replace each branch by an MQTT subscription when real sensors come online.
  useEffect(() => {
    const id = setInterval(() => {
      setSensorData(prev => {
        const battery = Math.max(0, Math.min(100,
          prev.battery + (Math.random() - 0.55) * 0.2));
        const motion = Math.random() < 0.08 ? 1 : 0;
        return { ...prev, battery, motion };
      });
    }, 3000);
    return () => clearInterval(id);
  }, []);

  const publishData = useCallback((topic, data) => {
    if (client && connected) {
      client.publish(topic, JSON.stringify(data), { qos: 1 }, (err) => {
        if (err) console.error('Publish error:', err);
      });
    }
  }, [client, connected]);

  const value = {
    connected,
    sensorData,
    history,
    status,
    geoStatus,
    publishData,
    TOPICS
  };

  return (
    <MQTTContext.Provider value={value}>
      {children}
    </MQTTContext.Provider>
  );
};

export const useMQTT = () => {
  const context = useContext(MQTTContext);
  if (!context) {
    throw new Error('useMQTT must be used within MQTTProvider');
  }
  return context;
};
